import React from 'react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../contexts/ThemeContext';
import { LastSyncDateDisplay } from './LastSyncDateDisplay';
import type { LastSyncInfo } from '../utils/lastSyncHint';

const SyncStatusIcon = ({ syncing, className }: { syncing: boolean; className?: string }) =>
  syncing ? (
    <svg className={cn('w-3.5 h-3.5 animate-spin', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ) : (
    <svg className={cn('w-3.5 h-3.5', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

interface LastSyncFooterProps {
  info: LastSyncInfo;
  compact?: boolean;
  /** `appFooter` = global app footer bar (horizontal, no card). */
  variant?: 'sidebar' | 'appFooter';
}

export const LastSyncFooter: React.FC<LastSyncFooterProps> = ({
  info,
  compact = false,
  variant = 'sidebar',
}) => {
  const { colors } = useTheme();

  const statusTone =
    info.syncing
      ? 'syncing'
      : info.status === 'ok'
        ? 'ok'
        : 'idle';

  const iconWrapClass =
    statusTone === 'syncing'
      ? 'bg-amber-500/15 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300'
      : statusTone === 'ok'
        ? 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-300'
        : cn('bg-gray-200/80 text-gray-600 dark:bg-white/10 dark:text-slate-300');

  const cardClass = cn(
    'mx-2 mb-2 mt-2 rounded-xl border px-2.5 py-2 min-w-0',
    'bg-gray-50 border-gray-200/90',
    'dark:bg-white/[0.06] dark:border-white/10',
    info.syncing && 'ring-1 ring-amber-400/25 dark:ring-amber-400/30',
  );

  const title = info.subtitle ? `${info.ariaLabel} — ${info.subtitle}` : info.ariaLabel;

  if (variant === 'appFooter') {
    const headline = info.syncing ? 'Syncing' : 'Last sync';
    return (
      <div
        className="flex items-center gap-2 min-w-0 max-w-full"
        role="status"
        aria-live="polite"
        aria-label={info.ariaLabel}
        title={title}
      >
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            iconWrapClass,
          )}
          aria-hidden
        >
          <SyncStatusIcon syncing={info.syncing} />
        </span>
        <div className="flex min-w-0 flex-nowrap items-center gap-1.5 text-xs leading-none">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {headline}
          </span>
          <span className="shrink-0 text-slate-400 dark:text-slate-500" aria-hidden>
            ·
          </span>
          {info.syncing ? (
            <span className={cn('min-w-0 truncate font-medium tabular-nums', colors.text)}>
              {info.timeText || 'Checking mailboxes…'}
            </span>
          ) : info.lastSyncAt ? (
            <LastSyncDateDisplay
              lastSyncAt={info.lastSyncAt}
              accounts={info.accountsSnapshot}
              layout="inline"
            />
          ) : (
            <span className={cn('font-medium', colors.textMuted)}>{info.timeText}</span>
          )}
          {info.subtitle ? (
            <>
              <span className="shrink-0 text-slate-400 dark:text-slate-500" aria-hidden>
                ·
              </span>
              <span className="min-w-0 truncate text-[10px] text-slate-500 dark:text-slate-400">
                {info.subtitle}
              </span>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={cn('flex-shrink-0 border-t px-1.5 py-2 flex items-center justify-center', colors.border)}
        title={title}
      >
        <span
          className={cn(
            'inline-flex items-center justify-center w-9 h-9 rounded-lg',
            iconWrapClass,
            info.syncing && 'ring-2 ring-amber-400/25',
          )}
          aria-label={info.ariaLabel}
        >
          <SyncStatusIcon syncing={info.syncing} />
        </span>
      </div>
    );
  }

  const headline = info.syncing ? 'Syncing' : 'Last sync';

  return (
    <div className={cn('flex-shrink-0 border-t', colors.border)}>
      <div
        className={cardClass}
        role="status"
        aria-live="polite"
        aria-label={info.ariaLabel}
        title={title}
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <span
            className={cn(
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              iconWrapClass,
            )}
            aria-hidden
          >
            <SyncStatusIcon syncing={info.syncing} />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {headline}
            </p>

            {info.syncing ? (
              <p className={cn('text-sm font-medium mt-0.5', colors.text)}>
                {info.timeText || 'Checking mailboxes…'}
              </p>
            ) : info.lastSyncAt ? (
              <div className="mt-0.5">
                <LastSyncDateDisplay
                  lastSyncAt={info.lastSyncAt}
                  accounts={info.accountsSnapshot}
                />
              </div>
            ) : (
              <p className={cn('text-sm font-medium mt-0.5', colors.textMuted)}>{info.timeText}</p>
            )}

            {info.subtitle ? (
              <p className="mt-1 truncate text-[10px] text-slate-500 dark:text-slate-400">{info.subtitle}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
